import { GraphConfiguration } from "@/type/common";
import { DomainRule } from "../Domain";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown } from "lucide-react";
import useSearch from "@/hooks/use-search";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import GraphConfigContext from "@/store/contexts/GraphConfigContext";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import SimpleSelect from "@/components/ui/simple-select";

const ManyToOnePicker = ({
  config,
  rule,
  onChange,
}: {
  config: GraphConfiguration | null;
  rule: DomainRule;
  onChange: (value: any) => void;
}) => {
  const { fields } = useContext(GraphConfigContext);
  const [recordType, setRecordType] = useState(
    rule.value && typeof rule.value === "string" && rule.value?.startsWith("@@")
      ? "relative"
      : "fixed"
  );
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const timeout = useRef<NodeJS.Timeout | null>(null);

  const model = useMemo(() => {
    // Use fieldObject.model if available (for nested fields), otherwise find in root fields
    return (
      rule.fieldObject?.model ||
      fields.find((f: any) => f.field === rule.field)?.model
    );
  }, [fields, rule.field, rule.fieldObject]);

  const {
    data: records,
    search: searchRecords,
    updateUrl,
  } = useSearch(`/get/model_search/${model}`, "");

  useEffect(() => {
    updateUrl(`/get/model_search/${model}`);
  }, [model]);

  useEffect(() => {
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    timeout.current = setTimeout(() => {
      searchRecords(search);
    }, 500);
  }, [search]);

  return (
    <div className="flex flex-row gap-2">
      {["res.users", "res.partner"].includes(model) && (
        <SimpleSelect
          className="w-32"
          value={recordType}
          onChange={(value) => {
            setRecordType(value);
            onChange(
              value === "relative"
                ? model === "res.users"
                  ? "@@me"
                  : "@@my_partner"
                : ""
            );
          }}
          options={[
            { value: "fixed", label: "Fixed" },
            { value: "relative", label: "Me" },
          ]}
        />
      )}
      {(recordType === "fixed" ||
        !["res.users", "res.partner"].includes(model)) && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="justify-between flex-1">
              <span>
                {rule.value ? (
                  rule.value
                ) : (
                  <span className="text-muted-foreground">Select a record</span>
                )}
              </span>
              <ChevronsUpDown className="opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 pb-1" align="start">
            <div className="mb-2 border-b">
              <Input
                placeholder="Search..."
                className="border-none focus-visible:border-none focus-visible:ring-0 shadow-none"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <ScrollArea className="h-[200px] px-1">
              <div className="flex flex-col">
                {records?.map((record: any) => (
                  <div
                    onClick={() => {
                      onChange(record.id);
                      setOpen(false);
                    }}
                    key={record.id}
                    className="flex items-center cursor-pointer hover:bg-accent rounded-sm p-1.5"
                  >
                    <span className="text-sm">{record.name}</span>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default ManyToOnePicker;
